import { h, Component, Prop, State, Watch } from '@stencil/core';

import { hasSlotContent } from '../../..';

@Component({
  tag: 'pk-sequence',
  styleUrl: './scss/pk-sequence.scss',
  shadow: true,
})
export class PkSequence {
  // Own Properties
  // ====================

  private itemSelector: string = 'pk-sequence-item';
  private containerElem: HTMLDivElement;
  private titleElem: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @State() hasTitle: boolean;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Will set how the sequence items children will look and behave, as timeline or step */
  @Prop({ reflect: true }) type: 'timeline' | 'sequence' | 'stepper' = 'timeline';

  /** If true, the vertical time line of the last `pk-sequence-item` won't be displayed */
  @Prop({ reflect: true }) disableLastItemLine: boolean = false;

  // Prop lifecycle events
  // =======================

  @Watch('disableLastItemLine')
  handleDisableLastItemLineChange() {
    this.handleLastItem();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.setItemType();
    this.handleLastItem();

    this.handleTitleSlotContent();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private getAllItems = (): HTMLPkSequenceItemElement[] => {
    const slot = this.containerElem.querySelector('slot');
    return [...slot.assignedElements({ flatten: true })].filter(
      (el: HTMLPkSequenceItemElement) => el.tagName.toLowerCase() === this.itemSelector,
    ) as [HTMLPkSequenceItemElement];
  };

  private setItemType = () => {
    this.getAllItems().forEach((seqItem: HTMLPkSequenceItemElement) => {
      seqItem.type = this.type;
    });
  };

  private getLastItem = () => {
    const allSequenceItems: HTMLPkSequenceItemElement[] = this.getAllItems();
    if (!allSequenceItems.length) return;

    return allSequenceItems[allSequenceItems.length - 1];
  };

  private handleLastItem = () => {
    const lastSequenceItem: HTMLPkSequenceItemElement = this.getLastItem();
    if (!lastSequenceItem) return;

    lastSequenceItem.last = true;
    // Disabled the item vertical line if it has been set in the root parent element
    lastSequenceItem.disableLine = this.disableLastItemLine;
  };

  private handleTitleSlotContent = () => {
    if (!this.titleElem) return;

    this.hasTitle = hasSlotContent(this.titleElem, 'heading');
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div class="block" part="base">
        <div
          class={{ 'pk-sequence__heading m-0 mb-l p-0': true, hidden: !this.hasTitle }}
          ref={(divElem: HTMLDivElement) => (this.titleElem = divElem)}
          part="heading"
        >
          <slot name="heading" onSlotchange={this.handleTitleSlotContent} />
        </div>
        <div class="flex flex-col" ref={(divElem: HTMLDivElement) => (this.containerElem = divElem)} part="container">
          <slot />
        </div>
      </div>
    );
  }
}
